import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

import {
  Button,
  IconButton,
  TextField,
  Typography,
} from '@zendesk/forethought-elements';
import { ConfirmationView } from './ConfirmationView';
import {
  COMMENT_MAX_LENGTH,
  COMMENT_MIN_LENGTH,
  getRatingOptions,
} from './conversationReview';
import { useSubmitConversationReviewMutation } from 'src/services/insights';
import { ConversationReviewRating } from 'src/services/insights/types';

interface FeedbackPanelProps {
  conversationId: string;
  initialComment?: string | null;
  initialRating?: ConversationReviewRating;
  nextConversationId?: string;
  onClose: () => void;
  onSelectConversation?: (conversationId: string) => void;
  previousConversationId?: string;
  queueLength?: number;
  queuePosition?: number;
}

export const FeedbackPanel = ({
  conversationId,
  initialComment,
  initialRating,
  nextConversationId,
  onClose,
  onSelectConversation,
  previousConversationId,
  queueLength = 0,
  queuePosition,
}: FeedbackPanelProps) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const ratingOptions = getRatingOptions(t);
  const [rating, setRating] = useState<ConversationReviewRating | undefined>(
    initialRating,
  );
  const [comment, setComment] = useState<string>(initialComment ?? '');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isExplicitSubmitting, setIsExplicitSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitReview] = useSubmitConversationReviewMutation();

  // Track the last values we successfully sent so the auto-save effect
  // doesn't re-fire on the initial-mount sync, and so editing back to the
  // saved value doesn't trigger a redundant write. Initialized from the
  // prefill props; the parent unmounts/remounts the panel between opens
  // so we don't need a useEffect to resync on prop changes.
  const lastSavedRating = useRef<ConversationReviewRating | undefined>(
    initialRating,
  );
  const lastSavedComment = useRef<string>((initialComment ?? '').trim());

  const isCommentRequired = rating === 'missed_the_mark';
  const trimmedComment = comment.trim();
  // A comment that's started but hasn't reached the minimum length yet —
  // surfaced as an inline warning and blocks submit, but auto-save still
  // runs so the user doesn't lose typing if they navigate away.
  const isCommentTooShort =
    trimmedComment.length > 0 && trimmedComment.length < COMMENT_MIN_LENGTH;
  // The backend rejects PUTs without a rating, rejects negative submissions
  // without a comment, and rejects submitted comments under the minimum
  // length. Mirror all three client-side so the user never sees a
  // server-side 422 they could have avoided.
  const canSubmit =
    Boolean(rating) &&
    (!isCommentRequired || trimmedComment.length > 0) &&
    !isCommentTooShort &&
    !isExplicitSubmitting;

  const autoSave = useCallback(
    async (
      newRating: ConversationReviewRating | undefined,
      newComment: string,
    ) => {
      const trimmed = newComment.trim();
      // The backend requires a rating on every PUT; until one is picked
      // there's nothing valid to auto-save.
      if (!newRating) {
        return;
      }
      // The backend rejects auto-saves on missed_the_mark with a 400 — a
      // negative review must be explicitly submitted with a comment.
      if (newRating === 'missed_the_mark') {
        return;
      }
      // Skip if the values haven't changed since the last successful save.
      if (
        newRating === lastSavedRating.current &&
        trimmed === lastSavedComment.current
      ) {
        return;
      }
      try {
        // Sync the "last saved" cache from the response, not optimistically
        // from intent. The backend returns the existing record (without
        // applying our write) if the review was already submitted, so the
        // response is the source of truth for what's actually persisted —
        // updating refs from the response keeps subsequent auto-save dedup
        // checks honest and reflects the BE's truth-telling behavior.
        const response = await submitReview({
          comment: trimmed.length > 0 ? trimmed : null,
          conversation_id: conversationId,
          is_submitted: false,
          rating: newRating,
        }).unwrap();
        lastSavedRating.current = response.rating;
        lastSavedComment.current = (response.comment ?? '').trim();
      } catch {
        // BE rejected — leave the "last saved" refs alone so the next
        // change attempt re-tries instead of being silently deduped.
      }
    },
    [conversationId, submitReview],
  );

  // Auto-save the rating immediately so a click-and-leave still captures it
  // (positive ratings only — see comment in autoSave above).
  const handleRatingClick = (newRating: ConversationReviewRating) => {
    setRating(newRating);
    autoSave(newRating, comment);
  };

  // Auto-save the comment after a short debounce while the user is typing.
  useEffect(() => {
    if (trimmedComment === lastSavedComment.current) {
      return;
    }
    const timer = setTimeout(() => {
      autoSave(rating, comment);
    }, 600);
    return () => clearTimeout(timer);
  }, [comment, rating, trimmedComment, autoSave]);

  const handleSubmit = async () => {
    if (!canSubmit || !rating) {
      return;
    }
    setIsExplicitSubmitting(true);
    setSubmitError(null);
    try {
      await submitReview({
        comment: trimmedComment.length > 0 ? trimmedComment : null,
        conversation_id: conversationId,
        is_submitted: true,
        rating,
      }).unwrap();
      lastSavedRating.current = rating;
      lastSavedComment.current = trimmedComment;
      setIsSubmitted(true);
    } catch {
      // TODO(AA-3103): surface server-side validation messages once the BE
      // returns structured errors. For now show a generic retry hint so the
      // failure isn't silent.
      setSubmitError(t('feedbackPanel.submitError'));
    } finally {
      setIsExplicitSubmitting(false);
    }
  };

  // Show prev/next navigation whenever there are flagged conversations to
  // review. When the current conversation sits inside the queue we render
  // its position ("2 of 10"); when it doesn't, we render the queue size
  // ("10 flagged") and Next jumps the user into the queue at the first
  // flagged conversation, letting them start a review session from any row.
  const showQueuePagination = queueLength > 0;
  const canGoPrevious = Boolean(previousConversationId);
  const canGoNext = Boolean(nextConversationId);
  const queueLabel =
    queuePosition !== undefined
      ? `${queuePosition} of ${queueLength}`
      : `${queueLength} flagged`;
  const handleReviewNext = nextConversationId
    ? () => onSelectConversation?.(nextConversationId)
    : undefined;

  return (
    <PanelContainer>
      <Header>
        <Typography variant='font18Bold'>{t('feedbackPanel.title')}</Typography>
        {/* Nudged left so the queue pagination clears the Drawer's close (X)
            button, which is absolutely positioned at the top-right. */}
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: '4px',
            marginRight: '50px',
          }}
        >
          {showQueuePagination && (
            <>
              <IconButton
                aria-label={t('feedbackPanel.previousConversationAriaLabel')}
                disabled={!canGoPrevious}
                onClick={() =>
                  previousConversationId &&
                  onSelectConversation?.(previousConversationId)
                }
                variant='ghost'
              >
                <IconChevronLeft size={16} />
              </IconButton>
              <Typography
                color={palette.colors.grey[600]}
                variant='font12Medium'
              >
                {queueLabel}
              </Typography>
              <IconButton
                aria-label={t('feedbackPanel.nextConversationAriaLabel')}
                disabled={!canGoNext}
                onClick={() =>
                  nextConversationId &&
                  onSelectConversation?.(nextConversationId)
                }
                variant='ghost'
              >
                <IconChevronRight size={16} />
              </IconButton>
            </>
          )}
          {/* The Drawer's own close button sits directly over this position
              and already dismisses the panel along with the drawer, so a
              dedicated panel close button would only stack on top of it.
              `onClose` is still wired up — ConfirmationView calls it after
              submission to dismiss the panel without closing the drawer. */}
        </Box>
      </Header>

      {isSubmitted && rating ? (
        <ConfirmationView
          onClose={onClose}
          onReviewNext={handleReviewNext}
          rating={rating}
        />
      ) : (
        <>
          <Typography color={palette.colors.grey[700]} variant='font14'>
            {t('feedbackPanel.description')}
          </Typography>

          <Section>
            <Typography variant='font14Bold'>
              {t('feedbackPanel.howDidIDo')}
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px' }}>
              {ratingOptions.map(option => {
                const isSelected = rating === option.value;
                return (
                  <RatingTile
                    aria-label={option.label}
                    aria-pressed={isSelected}
                    isSelected={isSelected}
                    key={option.value}
                    onClick={() => handleRatingClick(option.value)}
                    rating={option.value}
                    type='button'
                  >
                    <Box sx={{ fontSize: '32px', lineHeight: 1 }}>
                      {option.emoji}
                    </Box>
                    <Typography
                      color={palette.colors.grey[700]}
                      variant={isSelected ? 'font14Bold' : 'font14'}
                    >
                      {option.label}
                    </Typography>
                  </RatingTile>
                );
              })}
            </Box>
          </Section>

          <Section>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant='font14Bold'>
                {t('feedbackPanel.addComment')}
                {isCommentRequired ? ' *' : ''}
              </Typography>
              <Typography
                color={palette.colors.grey[600]}
                variant='font12Medium'
              >
                {t('feedbackPanel.characterCount', {
                  current: trimmedComment.length,
                  max: COMMENT_MAX_LENGTH,
                })}
              </Typography>
            </Box>
            <TextField
              aria-label={t('feedbackPanel.commentAriaLabel')}
              inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
              multiline
              onChange={e => setComment(e.target.value)}
              placeholder={t('feedbackPanel.commentPlaceholder')}
              rows={4}
              value={comment}
            />
            {isCommentRequired && trimmedComment.length === 0 ? (
              <Typography color={palette.colors.grey[600]} variant='font12'>
                {t('feedbackPanel.commentRequiredHint')}
              </Typography>
            ) : isCommentTooShort ? (
              <Typography color={palette.colors.red[500]} variant='font12'>
                {t('feedbackPanel.commentMinLengthError', {
                  count: COMMENT_MIN_LENGTH,
                })}
              </Typography>
            ) : null}
          </Section>

          {submitError && (
            <Typography color={palette.colors.red[500]} variant='font14'>
              {submitError}
            </Typography>
          )}

          <Button
            disabled={!canSubmit}
            fullWidth
            isLoading={isExplicitSubmitting}
            onClick={handleSubmit}
            variant='main'
          >
            {t('feedbackPanel.submitButton')}
          </Button>
        </>
      )}
    </PanelContainer>
  );
};

const PanelContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 40px;
  width: 586px;
`;

const Header = styled(Box)`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
`;

const Section = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RatingTile = styled('button')<{
  isSelected: boolean;
  rating: ConversationReviewRating;
}>`
  align-items: center;
  background: ${({ isSelected, rating, theme }) => {
    if (!isSelected) {
      return 'transparent';
    }
    return rating === 'good_enough'
      ? theme.palette.colors.blue[100]
      : theme.palette.colors.yellow[100];
  }};
  border: 1px solid
    ${({ isSelected, rating, theme }) => {
      if (!isSelected) {
        return theme.palette.colors.slate[200];
      }
      return rating === 'good_enough'
        ? theme.palette.colors.blue[500]
        : theme.palette.colors.yellow[500];
    }};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  padding: 24px 16px;
  transition:
    background 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: ${({ isSelected, rating, theme }) => {
      if (!isSelected) {
        return theme.palette.colors.slate[100];
      }
      return rating === 'good_enough'
        ? theme.palette.colors.blue[100]
        : theme.palette.colors.yellow[100];
    }};
  }
`;
